/**
 * Wraps a reducer with undo/redo.
 *
 * `deepSet` clones on every write, so each history entry is a full config tree
 * rather than a structural share. HISTORY_LIMIT keeps that bounded.
 *
 * Three action types are handled here and never reach the base reducer: UNDO,
 * REDO and CLEAR_HISTORY. RESET does reach it, but clears history afterwards —
 * it also wipes localStorage, so there is no coherent earlier state to return
 * to, which is why Reset is the one editor action that still asks for
 * confirmation.
 */

export const HISTORY_LIMIT = 30;

// Changes state but is not an edit the user would expect to undo.
const NON_RECORDING = new Set(['SET_LANGUAGE']);

export function initialHistory(present) {
  return { past: [], present, future: [] };
}

export function createHistoryReducer(baseReducer, limit = HISTORY_LIMIT) {
  return function historyReducer(state, action) {
    const { past, present, future } = state;

    if (action.type === 'UNDO') {
      if (past.length === 0) return state;
      return {
        past: past.slice(0, -1),
        present: past[past.length - 1],
        future: [present, ...future],
      };
    }

    if (action.type === 'REDO') {
      if (future.length === 0) return state;
      return {
        past: [...past, present],
        present: future[0],
        future: future.slice(1),
      };
    }

    if (action.type === 'CLEAR_HISTORY') {
      if (past.length === 0 && future.length === 0) return state;
      return { past: [], present, future: [] };
    }

    const next = baseReducer(present, action);

    // The base reducer ignored it (unknown type, or a guard bailed out).
    if (next === present) return state;

    if (action.type === 'RESET') {
      return { past: [], present: next, future: [] };
    }

    if (NON_RECORDING.has(action.type)) {
      return { past, present: next, future };
    }

    return {
      past: [...past, present].slice(-limit),
      present: next,
      future: [],
    };
  };
}
